import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import {
  useDeleteSavedRoute,
  useSavedRoutes,
} from '@/hooks/admin/useSavedRoutes';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
//import MapWithAutocomplete from '@/components/MapWithAutocomplete';
import MapStadiaWithAutocomplete from '@/components/MapStadiaWithAutocomplete';
import ArrowLeftIcon from '@/components/icons/ArrowAltLeftIcon';
import { useGetVehiclesByCompany } from '@/hooks/customer/useVehicles';
import { useGetConfigurationsByCompany } from '@/hooks/customer/useConfigurations';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNZTA } from '@/hooks/admin/useNzta';
import jsPDF from 'jspdf';
import { callAddFont } from '@/Roboto-Regular-normal.js';
import { callAddBoldFont } from '@/Roboto-Bold-bold.js';
import { callAddItalicFont } from '@/Roboto-Italic-italic.js';
import { useAuthUser } from '@/hooks/auth/useAuth';
import { useColorMode } from '@/styles/theme';
import { Document, Packer, Paragraph, TextRun, AlignmentType } from 'docx';
import DescriptionIcon from '@mui/icons-material/Description';

const accessToken =
  'pk.eyJ1Ijoib3JjaGlkd2ViIiwiYSI6ImNtOG1jcWM5cjBiNzYycXBtbWhzMXI5YW0ifQ.tu_IuquB3UhBfXataJpe4w';

export default function SavedRoutes() {
  const navigate = useNavigate();
  const { mode } = useColorMode();
  const { companyId, configurationId, savedRouteId } = useParams();
  const { data: authUser } = useAuthUser();
  const company = authUser?.currentCompany || '0';
  const { data: configurations } = useGetConfigurationsByCompany(
    String(company),
  );
  const { data: vehicles } = useGetVehiclesByCompany(String(company));
  const { data: savedRoutes, isLoading: isSavedRoutesLoading } =
    useSavedRoutes();
  const { data: nztaData, isLoading: isNztaDataLoading } = useNZTA();
  const [displayLeftBar, setDisplayLeftBar] = useState<boolean>(true);
  const [selectedSavedRouteId] = useState<any>(savedRouteId);

  const deleteSavedRoute = useDeleteSavedRoute();

  const isLoading = isSavedRoutesLoading || isNztaDataLoading;
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const routesForConfiguration = savedRoutes?.filter(
    (route) => route.configuration === configurationId,
  );

  const configuration = configurations?.find((c) => c._id === configurationId);
  const vehicle = vehicles?.find((v) => v._id === configuration?.vehicle);

  const validPermits =
    configuration?.permits.filter((permit: any) => {
      if (!permit.expiryDate) return true;
      const expiryDate = new Date(permit.expiryDate);
      const currentDate = new Date();
      return expiryDate >= currentDate;
    }) || [];
  const geojsonData = validPermits.map((p) => p.route) || [];

  const councilIds: any[] =
    validPermits
      .filter((permit: any) => permit?.councils)
      .map((permit: any) => permit.councils)
      .flat() || [];

  const selectedConfigurationRcas =
    validPermits.map((permit) => permit.rca) || [];

  const councils: any[] =
    nztaData
      ?.filter((nzta) => selectedConfigurationRcas?.find((s) => s === nzta._id))
      .map((nzta) => nzta.councils)
      .flat()
      .filter((council) => councilIds.find((c) => c._id === council._id))
      .map((c) => c.geojson) || [];

  const selectedRoute = savedRoutes?.find(
    (route) => route._id === savedRouteId,
  );

  const fetchRouteDistanceAndDuration = async (
    route: any,
  ): Promise<{ distance: number; duration: number }> => {
    if (!route.start || !route.end) {
      return { distance: 0, duration: 0 };
    }

    try {
      const response = await fetch('/api/v1/customer/map-route-distance/v1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ route }),
      });

      if (!response.ok) {
        console.error(
          'Error fetching route distance and duration:',
          response.statusText,
        );
        return { distance: 0, duration: 0 };
      }

      const data = await response.json();
      return {
        distance: data.distance || 0, // already in km from server
        duration: data.duration || 0, // in seconds from server
      };
    } catch (error) {
      console.error('Error fetching distance and duration:', error);
      return { distance: 0, duration: 0 };
    }
  };

  const fetchTurnInstructionsForRoute = async (
    route: any,
  ): Promise<string[]> => {
    if (!route.start || !route.end) {
      return [];
    }

    try {
      const response = await fetch(
        '/api/v1/customer/map-turn-instructions/v1',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ route }),
        },
      );

      if (!response.ok) {
        console.error('Error fetching turn instructions:', response.statusText);
        return [];
      }

      const data = await response.json();
      return data.instructions || [];
    } catch (error) {
      console.error('Error fetching turn instructions:', error);
      return [];
    }
  };

  const generatePDF = (
    route: any,
    instructions: string[],
    distance: number,
    duration: number,
  ) => {
    jsPDF.API.events.push(['addFonts', callAddFont]);
    jsPDF.API.events.push(['addFonts', callAddBoldFont]);
    jsPDF.API.events.push(['addFonts', callAddItalicFont]);

    const doc = new jsPDF({
      unit: 'mm',
      format: 'a4',
    });

    const headingFontSize = 16;
    doc.setFont('Roboto-Bold', 'bold');
    doc.setFontSize(headingFontSize);

    let currentY = 20;
    const margin = 10;
    const headingLineHeight = 1 * headingFontSize;

    doc.text(`Permit Pilot - Route Export`, margin, currentY);
    currentY += headingLineHeight;

    const detailsFontSize = 12;
    doc.setFont('Roboto-Italic', 'italic');
    doc.setFontSize(detailsFontSize);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const effectiveWidth = pageWidth - 2 * margin;
    const detaiLineHeight = 1 * detailsFontSize;

    const vehicle = vehicles?.find((v) => configuration?.vehicle === v._id);

    doc.text(
      vehicle ? `Vehicle: #${vehicle?.rego} - ${vehicle?.fleetNumber}` : 'N/A',
      margin,
      currentY,
    );
    currentY += detaiLineHeight;
    doc.text(
      `Configuration: ${configuration?.name || 'N/A'}`,
      margin,
      currentY,
    );
    currentY += detaiLineHeight;
    doc.text(`Start: ${route.start.name}`, margin, currentY);
    currentY += detaiLineHeight;
    doc.text(`End: ${route.end.name}`, margin, currentY);
    currentY += detaiLineHeight;
    doc.text(`Total Distance: ${distance.toFixed(2)} km`, margin, currentY);
    currentY += detaiLineHeight;

    let durationText = '';
    if (duration >= 3600) {
      const hours = Math.floor(duration / 3600);
      const remainingMinutes = Math.floor((duration % 3600) / 60);
      const remainingSeconds = Math.floor(duration % 60);

      durationText = `${hours} ${hours === 1 ? 'hour' : 'hours'}`;

      if (remainingMinutes > 0 || remainingSeconds > 0) {
        durationText += ` ${remainingMinutes} ${remainingMinutes === 1 ? 'min' : 'mins'}`;
      }

      if (remainingSeconds > 0) {
        durationText += ` ${remainingSeconds} ${remainingSeconds === 1 ? 'sec' : 'secs'}`;
      }
    } else {
      const minutes = Math.floor(duration / 60);
      const seconds = Math.floor(duration % 60);

      durationText = `${minutes} ${minutes === 1 ? 'min' : 'mins'}`;

      if (seconds > 0) {
        durationText += ` ${seconds} ${seconds === 1 ? 'sec' : 'secs'}`;
      }
    }
    doc.text(`Estimated Duration: ${durationText}`, margin, currentY);
    currentY += detaiLineHeight + 2.5;

    const instructionsFontSize = 12;
    doc.setFontSize(instructionsFontSize);
    doc.setFont('Roboto-Regular', 'normal');
    const instructionsLineHeight = 0.5 * instructionsFontSize;

    instructions.forEach((instruction, idx) => {
      const instructionText = `${idx + 1}. ${instruction}`;
      const multiLines = doc.splitTextToSize(instructionText, effectiveWidth);

      if (
        currentY + multiLines.length * instructionsLineHeight >
        pageHeight - margin
      ) {
        doc.addPage();
        currentY = 20;
      }

      doc.text(multiLines, margin, currentY);
      currentY += multiLines.length * instructionsLineHeight + 5;
    });

    doc.save(`${route.start.name}-${route.end.name}.pdf`);
  };

  const handleGeneratePDF = async (route: any) => {
    const instructions = await fetchTurnInstructionsForRoute(route);
    const { distance, duration } = await fetchRouteDistanceAndDuration(route);
    if (instructions.length > 0) {
      generatePDF(route, instructions, distance, duration);
    } else {
      console.error('No instructions available to generate PDF.');
    }
  };

  const generateDOCX = (
    route: any,
    instructions: string[],
    distance: number,
    duration: number,
  ) => {
    const vehicle = vehicles?.find((v) => configuration?.vehicle === v._id);

    let durationText = '';
    if (duration >= 3600) {
      const hours = Math.floor(duration / 3600);
      const remainingMinutes = Math.floor((duration % 3600) / 60);
      const remainingSeconds = Math.floor(duration % 60);

      durationText = `${hours} ${hours === 1 ? 'hour' : 'hours'}`;

      if (remainingMinutes > 0 || remainingSeconds > 0) {
        durationText += ` ${remainingMinutes} ${remainingMinutes === 1 ? 'min' : 'mins'}`;
      }

      if (remainingSeconds > 0) {
        durationText += ` ${remainingSeconds} ${remainingSeconds === 1 ? 'sec' : 'secs'}`;
      }
    } else {
      const minutes = Math.floor(duration / 60);
      const seconds = Math.floor(duration % 60);

      durationText = `${minutes} ${minutes === 1 ? 'min' : 'mins'}`;

      if (seconds > 0) {
        durationText += ` ${seconds} ${seconds === 1 ? 'sec' : 'secs'}`;
      }
    }

    const doc = new Document({
      styles: {
        default: {
          document: {
            run: {
              font: 'Roboto',
            },
          },
        },
      },
      sections: [
        {
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Permit Pilot - Route Export',
                  bold: true,
                  size: 32,
                  font: 'Roboto',
                }),
              ],
              alignment: AlignmentType.LEFT,
            }),

            // Empty paragraph for spacing
            new Paragraph({
              children: [new TextRun({ text: '', size: 24 })],
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: vehicle
                    ? `Vehicle: #${vehicle?.rego} - ${vehicle?.fleetNumber}`
                    : 'Vehicle: N/A',
                  italics: true,
                  size: 24,
                  font: 'Roboto',
                }),
              ],
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `Configuration: ${configuration?.name || 'N/A'}`,
                  italics: true,
                  size: 24,
                  font: 'Roboto',
                }),
              ],
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `Start: ${route.start.name}`,
                  italics: true,
                  size: 24,
                  font: 'Roboto',
                }),
              ],
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `End: ${route.end.name}`,
                  italics: true,
                  size: 24,
                  font: 'Roboto',
                }),
              ],
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `Total Distance: ${distance.toFixed(2)} km`,
                  italics: true,
                  size: 24,
                  font: 'Roboto',
                }),
              ],
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `Estimated Duration: ${durationText}`,
                  italics: true,
                  size: 24,
                  font: 'Roboto',
                }),
              ],
            }),

            new Paragraph({
              children: [new TextRun({ text: '', size: 24 })],
            }),

            ...instructions.map(
              (instruction, idx) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${idx + 1}. ${instruction}`,
                      size: 24,
                      font: 'Roboto',
                      color: '000000',
                    }),
                  ],
                  spacing: {
                    after: 100,
                  },
                }),
            ),
          ],
        },
      ],
    });

    Packer.toBlob(doc).then((blob) => {
      const url = URL.createObjectURL(blob);
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute('href', url);
      downloadAnchorNode.setAttribute(
        'download',
        `${route.start.name}-${route.end.name}.docx`,
      );
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
      URL.revokeObjectURL(url);
    });
  };

  const handleGenerateDOCX = async (route: any) => {
    const instructions = await fetchTurnInstructionsForRoute(route);
    const { distance, duration } = await fetchRouteDistanceAndDuration(route);
    if (instructions.length > 0) {
      generateDOCX(route, instructions, distance, duration);
    } else {
      console.error('No instructions available to generate DOCX.');
    }
  };

  const switchLeftBar = () => {
    setDisplayLeftBar((prev) => !prev);
  };

  const handleDelete = (id: string) => {
    deleteSavedRoute.mutate(id);
  };

  return (
    <Box>
      <Grid container spacing={0}>
        {displayLeftBar && (
          <Grid
            item
            xs={4}
            sx={{
              height: '100vh',
              overflowY: 'auto',
            }}
          >
            <Box px={1} py={1}>
              <Box
                display="flex"
                justifyContent="start"
                alignItems="center"
                sx={{ marginTop: '-5px' }}
              >
                <IconButton onClick={() => navigate(`/configurations`)}>
                  <ArrowLeftIcon color="#202D38" />
                </IconButton>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontSize: '14px',
                    textAlign: 'center',
                    marginLeft: '2px',
                  }}
                >
                  {vehicle?.rego}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    textAlign: 'center',
                    backgroundColor: '#444444',
                    color: '#fff',
                    borderRadius: '12px',
                    padding: '1px 10px',
                    marginLeft: '10px',
                  }}
                >
                  {configuration?.name}
                </Typography>
              </Box>

              {routesForConfiguration?.map((route: any) => (
                <Box
                  display="flex"
                  justifyContent="start"
                  alignItems="center"
                  sx={{
                    marginTop: '10px',
                    marginLeft: '6px',
                    background: mode === 'dark' ? '#334657' : '#EEE !important',
                    borderRadius: '6px',
                    border:
                      selectedSavedRouteId === route._id
                        ? '1.5px solid #F6AE2D'
                        : 'none',
                  }}
                  onClick={() =>
                    navigate(
                      `/configurations/${configurationId}/saved-routes/${route._id}`,
                    )
                  }
                  key={route._id}
                >
                  <Box
                    sx={{
                      padding: '10px 0 10px 10px',
                      width: '40%',
                    }}
                  >
                    <Typography sx={{ fontSize: '12px', textAlign: 'left' }}>
                      {/* STEVE THOMASON */}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', textAlign: 'left' }}>
                      {format(
                        new Date(route.updatedAt),
                        'hh:mma, dd-MMM-yyyy',
                      ).toUpperCase()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      padding: '10px 0',
                      width: '30%',
                      marginLeft: '5px',
                    }}
                  >
                    <Typography sx={{ fontSize: '13px', textAlign: 'left' }}>
                      {route.start.name} - {route.end.name}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      padding: '10px 0',
                      marginLeft: '10%',
                    }}
                  >
                    <IconButton sx={{}} onClick={() => handleDelete(route._id)}>
                      <DeleteIcon />
                    </IconButton>
                    {/* <IconButton
                      sx={{}}
                      onClick={() => handleGeneratePDF(route)}
                    >
                      <PictureAsPdfIcon />
                    </IconButton> */}
                    <IconButton
                      sx={{}}
                      onClick={() => handleGenerateDOCX(route)}
                    >
                      <DescriptionIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>
        )}

        <Grid
          item
          xs={displayLeftBar ? 8 : 12}
          sx={{ height: '100vh', marginTop: '-20px' }}
        >
          {!isLoading ? (
            <MapStadiaWithAutocomplete
              company={String(companyId)}
              show50MaxData={
                selectedConfigurationRcas?.includes(
                  '67f300c6ad9e642314798147',
                ) || false
              }
              showHpmvData={
                selectedConfigurationRcas?.includes(
                  '6813022984065d0306faa7ac',
                ) || false
              }
              configuration={String(configurationId)}
              switchLeftBar={switchLeftBar}
              displayLeftBar={displayLeftBar}
              routeData={{
                geojsonData: [...geojsonData, ...councils],
                ...selectedRoute,
              }}
            />
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
}
